import {
  Component,
  Input,
  ChangeDetectionStrategy,
  OnDestroy,
  ChangeDetectorRef,
  Output,
  EventEmitter
} from '@angular/core'
import { LargeEventCardComponentProps } from './large-event-card.interfaces'
import { combineLatest, ReplaySubject } from 'rxjs'
import { map, shareReplay, startWith, tap } from 'rxjs/operators'
import { StreamResponseWithLiveEventId } from '../../watch/utility/watch.interfaces'

@Component({
  selector: 'flo-large-event-card',
  templateUrl: './large-event-card.component.html',
  styleUrls: ['./large-event-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LargeEventCardComponent implements OnDestroy {
  constructor(private readonly cdf: ChangeDetectorRef) {}

  @Input() geoBlockedIds: ReadonlyArray<number> | null
  @Output() streamDisplayed = new EventEmitter<number>()

  public ngOnDestroy$ = new ReplaySubject<void>(1)
  public props$ = new ReplaySubject<LargeEventCardComponentProps>(1)
  public elementInView$ = new ReplaySubject<boolean>(1)
  public previewStreamTokens$ = new ReplaySubject<ReadonlyArray<StreamResponseWithLiveEventId>>(1)

  @Input() public set props(props: LargeEventCardComponentProps | null) {
    if (props) {
      this.props$.next(props)
    }
  }

  @Input() public set previewStreamTokens(previewStreamTokens: ReadonlyArray<StreamResponseWithLiveEventId>) {
    if (previewStreamTokens) {
      this.previewStreamTokens$.next(previewStreamTokens)
    }
  }

  /**
   * When the video preview emits an error we switch back to the video thumbnail
   */
  public handleVideoError$ = new ReplaySubject<boolean>(1)

  /**
   * if no video error and if there is a previewToken available, we then find the token in the array that matches
   * the liveEventId associated with the event hydrating the event card. If no previewToken exists that means they
   * are not in the variation of the exp.
   *
   * TODO remove detectChanges() in the tap. Rxjs should be handling the view updating but for some reason
   * it does not update until hover on the screen. So video hangs around when it should have switched to
   * the thumbnail. Need to figure out why...
   */
  public isLivePreviewStreamAvailable$ = combineLatest([
    this.props$,
    this.previewStreamTokens$,
    this.handleVideoError$.pipe(startWith(false)),
    this.elementInView$.pipe(startWith(false))
  ]).pipe(
    map(
      ([props, previewArray, videoError, elementInView]: [
        LargeEventCardComponentProps,
        StreamResponseWithLiveEventId[],
        boolean,
        boolean
      ]): StreamResponseWithLiveEventId | null | undefined => {
        // Will return a StreamResponseWithLiveEventId if found
        // Null if not found
        // Undefined if pre-conditions fail
        if (!videoError && elementInView && previewArray) {
          return previewArray.find(a => a.liveEventId === props.liveEventId)
        }
        return
      }
    ),
    tap(liveStream => {
      if (liveStream?.liveEventId) {
        /**
         * Fire the streamDisplayed output if the preview stream is displayed
         */
        this.streamDisplayed.next(liveStream.liveEventId)
      }
      this.cdf.detectChanges()
    }),
    shareReplay(1)
  )

  ngOnDestroy() {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
